/**
 * AI Streaming Client
 * 
 * Unified client for all AI streaming endpoints using Server-Sent Events (SSE).
 * Handles connection, parsing, error recovery, and cleanup automatically.
 * 
 * Usage:
 *   const client = new AiStreamClient('/openings/stream_description');
 *   await client.stream({ title: 'Engineer' }, {
 *     onChunk: (data) => console.log(data),
 *     onComplete: () => console.log('Done'),
 *     onError: (err) => console.error(err)
 *   });
 * 
 * Features:
 * - Automatic CSRF token handling
 * - Graceful error recovery
 * - Connection abort support
 * - Reconnection on network failure (optional)
 * - Progress tracking
 */

export class AiStreamClient {
  constructor(endpoint, options = {}) {
    this.endpoint = endpoint;
    this.abortController = null;
    this.reader = null;  // Track reader for proper cleanup
    this.retryCount = 0;
    this.maxRetries = options.maxRetries || 0;  // No retries by default
    this.retryDelay = options.retryDelay || 1000;
  }
  
  /**
   * Start streaming from the endpoint
   * 
   * @param {Object} payload - Request payload
   * @param {Object} callbacks - Event callbacks
   * @param {Function} callbacks.onChunk - Called for each data chunk
   * @param {Function} callbacks.onComplete - Called on successful completion
   * @param {Function} callbacks.onError - Called on error
   * @param {Function} callbacks.onProgress - Called with progress updates (if available)
   * @param {Function} callbacks.onStart - Called when connection established
   */
  async stream(payload, callbacks = {}) {
    const { onChunk, onComplete, onError, onProgress, onStart } = callbacks;
    
    this.abortController = new AbortController();
    
    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }
      
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(payload),
        signal: this.abortController.signal
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      
      // Verify content type
      const contentType = response.headers.get('Content-Type');
      if (!contentType?.includes('text/event-stream')) {
        throw new Error(`Expected text/event-stream but got ${contentType}`);
      }
      
      onStart?.();
      
      await this._readStream(response.body, { onChunk, onProgress });
      this.reader = null;  // Clear reader reference on successful completion
      
      onComplete?.();
      this.retryCount = 0;  // Reset on success
      
    } catch (error) {
      // Clean up reader on error
      if (this.reader) {
        try {
          await this.reader.cancel();
        } catch (cancelError) {
          // Reader might already be closed, ignore error
        }
        this.reader = null;
      }

      if (error.name === 'AbortError') {
        // User cancelled, not an error
        return;
      }
      
      // Retry on network errors if configured
      if (this._shouldRetry(error)) {
        await this._sleep(this.retryDelay);
        this.retryCount++;
        return this.stream(payload, callbacks);
      }
      
      onError?.(error);
      throw error;
    }
  }
  
  /**
   * Read and parse the event stream
   * @private
   */
  async _readStream(body, { onChunk, onProgress }) {
    this.reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await this.reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';  // Keep incomplete line in buffer
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            // Handle error events from server
            if (data.error) {
              throw new Error(data.error);
            }
            
            // Handle progress updates
            if (data.progress !== undefined) {
              onProgress?.(data.progress);
            }
            
            // Handle data chunks
            if (data.text !== undefined || data.content !== undefined) {
              onChunk?.(data);
            } else {
              // Pass through raw data if not structured
              onChunk?.(data);
            }
            
          } catch (parseError) {
            console.warn('Failed to parse SSE data:', line, parseError);
          }
        } else if (line.startsWith('event: ')) {
          // Handle custom events if needed
          const eventType = line.slice(7);
          if (eventType === 'error') {
            throw new Error('Server sent error event');
          }
        }
        // Ignore comment lines (starting with ':') and empty lines
      }
    }
  }
  
  /**
   * Abort the current stream
   */
  abort() {
    // Cancel the reader first to release the stream lock
    if (this.reader) {
      this.reader.cancel().catch(() => {
        // Reader might already be closed, ignore error
      });
      this.reader = null;
    }
    
    // Then abort the fetch request
    this.abortController?.abort();
    this.abortController = null;
  }
  
  /**
   * Check if error should trigger a retry
   * @private
   */
  _shouldRetry(error) {
    if (this.retryCount >= this.maxRetries) return false;
    
    // Retry on network errors, not on application errors
    return error.name === 'TypeError' || 
           error.message.includes('network') ||
           error.message.includes('fetch');
  }
  
  /**
   * Sleep helper for retry delays
   * @private
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * Check if browser supports streaming
   */
  static isSupported() {
    return typeof ReadableStream !== 'undefined' && 
           typeof TextDecoder !== 'undefined';
  }
}

// Export as default for easier imports
export default AiStreamClient;
